import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { CasinoApiError, CasinoClient } from '@casino/sdk';

export interface Me {
  userId: string;
  tenantId: string;
  username: string;
  displayName: string;
  roles: string[];
  csrfToken: string | null;
}

let csrf: string | null = null;
/** Host-app API client (cookie session + CSRF header). */
export const api = new CasinoClient({ csrfToken: () => csrf });

interface SessionCtx {
  me: Me | null;
  loading: boolean;
  refresh(): Promise<void>;
  logout(): Promise<void>;
  balance: { balance: string; version: bigint } | null;
  setBalanceIfNewer(b: { balance: string; version: string }): void;
  /** While a game animates, the header holds the pre-settlement balance so wins are not revealed early. */
  holdBalance(hold: boolean): void;
  hasRole(...r: string[]): boolean;
}

const Ctx = createContext<SessionCtx | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
  const [me, setMe] = useState<Me | null>(null);
  const [loading, setLoading] = useState(true);
  const [balance, setBalance] = useState<{ balance: string; version: bigint } | null>(null);
  const held = useRef(false);
  const pending = useRef<{ balance: string; version: bigint } | null>(null);
  const latest = useRef<bigint>(-1n);

  const setBalanceIfNewer = useCallback((b: { balance: string; version: string }) => {
    const v = BigInt(b.version);
    if (v <= latest.current) return; // ignore duplicate/older versions
    latest.current = v;
    const next = { balance: b.balance, version: v };
    if (held.current) pending.current = next;
    else setBalance(next);
  }, []);

  const holdBalance = useCallback((hold: boolean) => {
    held.current = hold;
    if (!hold && pending.current) {
      setBalance(pending.current);
      pending.current = null;
    }
  }, []);

  const refresh = useCallback(async () => {
    try {
      const m = await api.request<Me>('GET', '/v1/me');
      csrf = m.csrfToken;
      setMe(m);
      const b = await api.getBalance();
      setBalanceIfNewer(b);
    } catch (e) {
      if (!(e instanceof CasinoApiError) || e.code !== 'UNAUTHENTICATED') console.warn(e);
      setMe(null);
    } finally {
      setLoading(false);
    }
  }, [setBalanceIfNewer]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  // Shared-balance updates over SSE (casino + sports share one authoritative balance).
  useEffect(() => {
    if (!me) return;
    let es: EventSource | null = null;
    let closed = false;
    const connect = () => {
      es = new EventSource('/v1/events', { withCredentials: true });
      es.addEventListener('balance', (ev) => {
        const d = JSON.parse((ev as MessageEvent).data);
        if (d.namespace === 'main') setBalanceIfNewer(d);
      });
      es.onerror = () => {
        es?.close();
        if (!closed) setTimeout(connect, 3000);
      };
    };
    connect();
    return () => {
      closed = true;
      es?.close();
    };
  }, [me, setBalanceIfNewer]);

  const logout = useCallback(async () => {
    await api.request('POST', '/v1/auth/logout', {}).catch(() => undefined);
    csrf = null;
    setMe(null);
    setBalance(null);
    latest.current = -1n;
  }, []);

  const value = useMemo<SessionCtx>(
    () => ({
      me,
      loading,
      refresh,
      logout,
      balance,
      setBalanceIfNewer,
      holdBalance,
      hasRole: (...r) => !!me && (me.roles.includes('admin') || r.some((x) => me.roles.includes(x))),
    }),
    [me, loading, refresh, logout, balance, setBalanceIfNewer, holdBalance],
  );
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): SessionCtx {
  const c = useContext(Ctx);
  if (!c) throw new Error('SessionProvider missing');
  return c;
}

export function setCsrf(token: string | null) {
  csrf = token;
}
